import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Radio, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { useNetworkScanJob } from '../contexts/NetworkScanJobContext';
import { toast } from 'sonner';
import { detectBrowserLanCidr } from '../lib/detectBrowserLan';
import { expandFirstIpv4ToSlash24, isBareSingleIpv4 } from '../lib/scanTargets';
import { NetworkScanLive, NetworkScanTerminalBanner } from '../components/NetworkScanLive';

/** Backend `device_type` codes → short labels for the discovery table */
const DEVICE_TYPE_LABELS = {
  // network_host: legacy code from older scans; same label as no_open_ports
  network_host: 'Reachable (no open ports on probe list)',
  no_open_ports: 'Reachable (no open ports on probe list)',
  printer: 'Printer / MFP',
  industrial_ot: 'Industrial / OT',
  nas_or_appliance: 'NAS / appliance',
  nas_or_file_server: 'NAS / file server',
  camera_or_nvr: 'Camera / NVR',
  windows_pc: 'Windows (RDP)',
  windows_host: 'Windows / SMB',
  phone_or_tablet: 'Phone / tablet (weak)',
  apple_device: 'Apple / AirPlay',
  linux_or_server: 'Linux server',
  linux_or_unix: 'Linux / Unix',
  network_device: 'Switch / router',
  desktop_or_server: 'Desktop (VNC)',
  generic_web_device: 'Web / unknown device',
  likely_android_device: 'Android (ADB)',
  likely_phone_or_tablet: 'Phone / tablet (Wi‑Fi)',
  apple_phone_tablet_or_mac: 'Apple (iPhone / iPad / Mac)',
  likely_laptop_or_desktop: 'Laptop / desktop (OEM)',
  likely_laptop_or_pc: 'Laptop / PC (NIC)',
  unknown: 'Unknown',
};

function deviceTypeLabel(code) {
  if (!code) return DEVICE_TYPE_LABELS.unknown;
  return DEVICE_TYPE_LABELS[code] || code.replace(/_/g, ' ');
}

export default function NetworkScanPage() {
  const { context } = usePilotContext();
  const { tracked, registerScan, loadJobForDisplay } = useNetworkScanJob();
  const [targets, setTargets] = useState('');
  /** Empty = API uses full built-in probe list (best for device-kind hints). */
  const [ports, setPorts] = useState('');
  const [autoIngest, setAutoIngest] = useState(true);
  const [includeNoPorts, setIncludeNoPorts] = useState(true);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detectLoading, setDetectLoading] = useState(true);
  const [detectSource, setDetectSource] = useState(null);
  const [detectHint, setDetectHint] = useState(null);

  const cid = context.customer_id;
  const scanServerOn = context.network_scan_enabled;
  const allowlistBlocksScans = context.network_scan_allowlist_gate_open === false;
  const allowlistRuleCount =
    typeof context.network_scan_allowlist_rule_count === 'number'
      ? context.network_scan_allowlist_rule_count
      : null;
  const active =
    tracked && tracked.customerId === cid
      ? tracked
      : null;
  const jobId = active?.jobId || '';
  const job = active?.job ?? null;
  const devices = active?.devices ?? null;
  const scanStartedAt = active?.scanStartedAt ?? null;
  const displayIncludeNoPorts = active?.includeNoPorts ?? includeNoPorts;
  const prevJobStatusRef = useRef(null);

  const runAutoDetect = useCallback(async () => {
    setDetectLoading(true);
    setDetectHint(null);
    try {
      const [browserCidr, server] = await Promise.all([
        detectBrowserLanCidr().catch(() => null),
        api.get('/api/network-scan/detect-subnets').catch(() => null),
      ]);
      if (browserCidr) {
        setTargets(browserCidr);
        setDetectSource('browser');
        setDetectHint(null);
      } else if (server?.recommended_cidr) {
        setTargets(server.recommended_cidr);
        setDetectSource(server.source === 'env' ? 'env' : 'server');
        setDetectHint(server.hint || null);
      } else {
        setTargets('');
        setDetectSource(null);
        setDetectHint(
          server?.hint ||
            'Could not detect a subnet automatically. Ask your admin to set AERTA_AUTO_SCAN_DEFAULT_CIDR or enter your LAN CIDR (e.g. 192.168.1.0/24).'
        );
      }
    } catch {
      setDetectSource(null);
      setDetectHint('Detection failed. Enter your LAN CIDR manually.');
    } finally {
      setDetectLoading(false);
    }
  }, []);

  useEffect(() => {
    runAutoDetect();
  }, [runAutoDetect]);

  const loadList = useCallback(async () => {
    if (!context.customer_id) return;
    try {
      const data = await api.get(`/api/network-scan/${context.customer_id}/jobs`);
      setList(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, [context.customer_id]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const st = job?.status;
    const prev = prevJobStatusRef.current;
    prevJobStatusRef.current = st;
    if ((prev === 'queued' || prev === 'running') && st === 'completed') {
      loadList();
    }
  }, [job?.status, loadList]);

  const queue = async () => {
    if (!context.customer_id) return;
    const tks = targets.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (tks.length === 0) {
      toast.error('No scan range yet — wait for auto-detect or enter a CIDR.');
      return;
    }
    const portList = ports
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    setLoading(true);
    try {
      const res = await api.post(`/api/network-scan/${context.customer_id}/jobs`, {
        targets: tks,
        ports: portList.length ? portList : undefined,
        auto_ingest: autoIngest,
        include_no_ports: includeNoPorts,
      });
      registerScan({
        jobId: res.job_id,
        customerId: context.customer_id,
        scanStartedAt: Date.now(),
        targetsLabel: tks.join(', '),
        includeNoPorts,
        initialJob: {
          job_id: res.job_id,
          status: 'queued',
          targets_json: tks,
          ports_json: portList.length ? portList : [],
        },
        devices: null,
      });
      toast.success('Scan queued — tracking live below');
      await loadList();
    } catch (e) {
      toast.error(e.message || 'Queue failed');
    } finally {
      setLoading(false);
    }
  };

  const refreshJob = async () => {
    if (!jobId || !context.customer_id) return;
    try {
      await loadJobForDisplay(context.customer_id, jobId);
    } catch (e) {
      toast.error(e.message || 'Status failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Network scan</h1>
        <p className="max-w-3xl text-muted-foreground">
          Discovery runs on the <strong className="text-foreground">API server</strong>, using the target
          list below (browser hint + server interfaces). Use a <strong className="text-foreground">subnet</strong>{' '}
          (e.g. <code className="text-xs">10.0.0.0/24</code>) so nmap can list many hosts; a{' '}
          <strong className="text-foreground">single IP</strong> only checks that one address. Requires{' '}
          <code className="text-xs">AERTA_SCAN_ENABLED=true</code> on the API.
        </p>
        {context.network_scan_cloud_api ? (
          <div className="mt-4 max-w-3xl rounded-md border border-sky-500/35 bg-sky-500/10 px-4 py-3 text-sm text-sky-950 dark:text-sky-100">
            <strong className="text-foreground">Hosted API:</strong> this scan does{' '}
            <strong>not</strong> normally run on your laptop or phone. By default it only sees networks the{' '}
            <em>server</em> can reach (usually the cloud provider&apos;s private network). Your home{' '}
            <code className="rounded bg-secondary/80 px-1 text-[11px]">192.168.x.x</code> range is typically{' '}
            <strong>not</strong> reachable from a hosted API unless you use a VPN/tunnel, deploy AERTA on‑prem, or run a{' '}
            <strong>lab connector</strong> inside that network. If a connector is online, AERTA will route scans through it.
          </div>
        ) : null}
        {!scanServerOn ? (
          <div className="mt-4 max-w-3xl rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
            Network scanning is <strong>off</strong> on the API. Set{' '}
            <code className="rounded bg-secondary px-1">AERTA_SCAN_ENABLED=true</code> on the server (e.g. Render
            env), redeploy, then reload this page.
          </div>
        ) : null}
        {scanServerOn && allowlistBlocksScans ? (
          <div className="mt-4 max-w-3xl rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <strong>Enterprise allowlist mode</strong> is on but no CIDRs are configured. Set{' '}
            <code className="rounded bg-secondary px-1">AERTA_SCAN_ALLOWLIST</code> to the comma-separated ranges you
            are authorized to scan, or ask your operator to set{' '}
            <code className="rounded bg-secondary px-1">AERTA_SCAN_ALLOWLIST_REQUIRED=false</code> for open pilot
            scanning. Parsed allowlist rules on the server:{' '}
            <strong>{allowlistRuleCount !== null ? allowlistRuleCount : '—'}</strong>.
          </div>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-primary" />
            Queue scan
          </CardTitle>
          <CardDescription>
            Detected scope is shown below — adjust if needed. Ports are optional (defaults on the server
            if empty).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {detectLoading ? (
            <p className="text-sm text-muted-foreground">Detecting connected network…</p>
          ) : (
            <div className="space-y-1 text-sm">
              <div className="text-muted-foreground">
                Source:{' '}
                {detectSource === 'browser' && (
                  <span className="text-foreground">This device (browser / WebRTC)</span>
                )}
                {detectSource === 'server' && (
                  <span className="text-foreground">API host interfaces</span>
                )}
                {detectSource === 'env' && (
                  <span className="text-foreground">AERTA_AUTO_SCAN_DEFAULT_CIDR</span>
                )}
                {!detectSource && <span className="text-foreground">—</span>}
              </div>
              {detectHint && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-900 dark:text-amber-100/90">
                  {detectHint}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => runAutoDetect()} disabled={detectLoading}>
              Re-detect network
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!isBareSingleIpv4(targets)}
              title="Turn one IPv4 into a /24 so the scanner can probe the whole segment (from the API host)"
              onClick={() => {
                const cidr = expandFirstIpv4ToSlash24(targets);
                if (cidr) {
                  setTargets(cidr);
                  toast.success(`Using subnet ${cidr} — queue a new scan to discover more hosts.`);
                }
              }}
            >
              Expand first IP to /24
            </Button>
          </div>
          {isBareSingleIpv4(targets) ? (
            <p className="text-xs text-amber-800/95 dark:text-amber-100/85">
              You&apos;re only scanning <strong>one</strong> address. Use &quot;Expand first IP to /24&quot; (or type{' '}
              <code className="rounded bg-secondary px-1">x.x.x.0/24</code>) to list other devices that respond on
              that segment <em>from the API host</em>.
            </p>
          ) : null}
          <textarea
            className="min-h-[100px] w-full rounded-md border border-border bg-secondary/30 p-3 font-mono text-sm"
            value={targets}
            onChange={(e) => setTargets(e.target.value)}
            placeholder="Auto-filled, e.g. 192.168.1.0/24"
          />
          <input
            className="w-full rounded-md border border-border bg-secondary/30 p-2 font-mono text-sm"
            value={ports}
            onChange={(e) => setPorts(e.target.value)}
            placeholder="Leave empty for full list, or e.g. 22,80,443,5432,8080"
          />
          <p className="text-xs text-muted-foreground">
            A short list (e.g. only four ports) often yields 0 open ports on every host, so every row
            shows &quot;Reachable (no open ports…)&quot; and no Printer/Windows-style kind.
          </p>
          <div className="rounded-md border border-border/80 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <strong className="text-foreground">Render / private Postgres:</strong> IPs like{' '}
            <code className="text-[11px]">10.25.x.x</code> with hostnames <code className="text-[11px]">srv-…</code>{' '}
            are usually <em>private services on the same cloud</em> (e.g. Postgres on{' '}
            <code className="text-[11px]">5432</code>), not your Fly Kali bridge. The scan runs from the{' '}
            <strong className="text-foreground">API</strong> host: in Render, link the web service to the database
            private service so TCP from the API to <code className="text-[11px]">5432</code> is allowed. After deploy,
            the default probe list on the server includes common DB ports (see below).
          </div>
          {Array.isArray(context.network_scan_default_ports) && context.network_scan_default_ports.length > 0 ? (
            <details className="rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs">
              <summary className="cursor-pointer font-medium text-foreground">
                Default TCP ports this API probes when the field above is empty (
                {context.network_scan_default_ports.length} ports)
              </summary>
              <p className="mt-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
                {context.network_scan_default_ports.join(', ')}
              </p>
            </details>
          ) : null}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoIngest}
              onChange={(e) => setAutoIngest(e.target.checked)}
            />
            Auto-ingest results into CMDB (same as discovery ingest)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeNoPorts}
              onChange={(e) => setIncludeNoPorts(e.target.checked)}
            />
            Include reachable hosts even when no probed ports are open (uses nmap `-sn` when available)
          </label>
          {includeNoPorts && (
            <p className="text-xs text-amber-800/90 dark:text-amber-100/80">
              Many rows may show only &quot;reachable&quot; with 0 open ports — device kind stays generic until
              at least one probed port responds (firewalls often block inbound scans).
            </p>
          )}
          <Button
            onClick={queue}
            disabled={
              loading ||
              detectLoading ||
              !targets.trim() ||
              !scanServerOn ||
              allowlistBlocksScans
            }
          >
            {loading ? 'Queueing…' : 'Start scan on this range'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Job status</CardTitle>
            <Button size="sm" variant="outline" onClick={refreshJob} disabled={!jobId}>
              <RefreshCw className="mr-1 h-4 w-4" />
              Refresh
            </Button>
          </div>
          <CardDescription>{jobId || 'Submit a job to see status'}</CardDescription>
        </CardHeader>
        <CardContent>
          {!jobId ? (
            <p className="text-sm text-muted-foreground">No active job — start a scan above.</p>
          ) : (
            <>
              {jobId && !job && (
                <p className="mb-3 text-sm text-muted-foreground">Loading job status…</p>
              )}
              <NetworkScanLive
                status={job?.status}
                jobId={jobId}
                targetsLabel={
                  (job?.targets_json && job.targets_json.join(', ')) ||
                  active?.targetsLabel ||
                  targets.trim()
                }
                includeNoPorts={displayIncludeNoPorts}
                startedAt={
                  scanStartedAt ??
                  (job?.created_at ? Date.parse(job.created_at) : null)
                }
              />
              <NetworkScanTerminalBanner status={job?.status} errorText={job?.error_text} />
              {job && (job.status === 'completed' || job.status === 'failed') && (
                <details className="group mb-3 rounded-md border border-border/50 bg-secondary/20">
                  <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
                    Raw job payload
                  </summary>
                  <pre className="max-h-[200px] overflow-auto border-t border-border/40 p-3 text-[11px] leading-relaxed">
                    {JSON.stringify(job, null, 2)}
                  </pre>
                </details>
              )}
              {job && job.status !== 'completed' && job.status !== 'failed' && (
                <pre className="mb-3 overflow-x-auto rounded-md bg-secondary/40 p-3 text-[11px] opacity-90">
                  {JSON.stringify(job, null, 2)}
                </pre>
              )}
            </>
          )}

          {job?.status === 'completed' && devices && (
            <div className="mt-4 space-y-2">
              <div className="text-sm font-semibold">Discovered hosts</div>
              {Array.isArray(job?.ports_json) && (
                <p className="text-xs text-muted-foreground">
                  Ports probed this run:{' '}
                  <span className="font-mono text-foreground">
                    {job.ports_json.length > 0 ? job.ports_json.join(', ') : 'Server default (full classification set)'}
                  </span>
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Kind uses <strong className="font-medium text-foreground">open TCP ports</strong>, then (when ports are
                silent) <strong className="font-medium text-foreground">MAC / vendor</strong> when the scanner shares a
                layer-2 segment with the target. On a <strong className="text-foreground">cloud</strong> API host, results
                are usually cloud-private peers — not your home Wi‑Fi. Apple/Samsung/Dell-style labels are coarse (MAC
                cannot name a specific laptop model).
              </p>
              {devices.length === 1 && context.network_scan_cloud_api ? (
                <p className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs text-sky-950 dark:text-sky-50/95">
                  A single row often means you scanned <strong>one IP</strong> or only one host answered on this network.
                  Go back to <strong>Queue scan</strong>, use <strong>Expand first IP to /24</strong> (or enter a{' '}
                  <code className="rounded bg-secondary/80 px-1">/24</code> range), and run again. To see your office or
                  home devices, the scanner must run <em>inside</em> that network (on-prem AERTA or VPN), not only in the
                  browser.
                </p>
              ) : null}
              {devices.length > 0 &&
                devices.every((d) => !(d.open_ports && d.open_ports.length)) && (
                  <p className="rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-xs text-amber-950 dark:text-amber-50/95">
                    Scan finished but all discovered hosts returned 0 open ports. Classification will stay generic until
                    at least one port responds. Try explicit ports (e.g. 22,80,443,3389), verify firewall rules, and
                    confirm services are reachable from the API host network.
                  </p>
                )}
              {devices.length === 0 ? (
                <p className="text-sm text-muted-foreground">No reachable hosts returned by discovery.</p>
              ) : (
                <div className="max-h-[320px] overflow-y-auto rounded-md border border-border/60">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 text-xs uppercase text-muted-foreground">
                        <th className="px-3 py-2 text-left">IP</th>
                        <th className="px-3 py-2 text-left">MAC</th>
                        <th className="px-3 py-2 text-left">Hostname</th>
                        <th className="px-3 py-2 text-left">Kind</th>
                        <th className="px-3 py-2 text-right">Open ports</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map((d) => (
                        <tr key={d.ip} className="border-b border-border/30 last:border-0">
                          <td className="px-3 py-2 font-mono">{d.ip}</td>
                          <td
                            className="max-w-[120px] truncate px-3 py-2 font-mono text-[11px]"
                            title={d.mac_address || ''}
                          >
                            {d.mac_address || '—'}
                          </td>
                          <td className="max-w-[120px] truncate px-3 py-2 text-xs" title={d.hostname || ''}>
                            {d.hostname || '—'}
                          </td>
                          <td
                            className="max-w-[200px] px-3 py-2 text-xs"
                            title={
                              [d.oui_vendor, d.classification_hint || d.device_type].filter(Boolean).join(' — ') ||
                              ''
                            }
                          >
                            {deviceTypeLabel(d.device_type)}
                            {d.oui_vendor ? (
                              <span className="ml-1 text-[10px] text-muted-foreground">({d.oui_vendor})</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {(d.open_ports || []).length}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground">No jobs yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {list.map((j) => (
                <li key={j.job_id} className="flex flex-wrap gap-2 border-b border-border py-2">
                  <span className="font-mono">{j.job_id}</span>
                  <span>{j.status}</span>
                  {j.devices_found != null ? <span>{j.devices_found} devices</span> : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    type="button"
                    onClick={async () => {
                      if (!context.customer_id) return;
                      if (j.status === 'queued' || j.status === 'running') {
                        registerScan({
                          jobId: j.job_id,
                          customerId: context.customer_id,
                          scanStartedAt: j.created_at ? Date.parse(j.created_at) : Date.now(),
                          targetsLabel: '',
                          includeNoPorts: true,
                          initialJob: { job_id: j.job_id, status: j.status },
                          devices: null,
                        });
                      } else {
                        try {
                          await loadJobForDisplay(context.customer_id, j.job_id);
                        } catch (e) {
                          toast.error(e.message || 'Load failed');
                        }
                      }
                    }}
                  >
                    Load
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
